import React from 'react';
import {
  AddConsultantButton,
  AddIcon,
  BackFullIcon,
  ConsultantsContainer,
  Container,
  Content,
  DescriptionRow,
  DescriptionText,
  Header,
  HeaderActions,
  HeaderBack,
  HeaderInfo,
  HeaderTitle,
  LoadRow,
} from './styles';
import Load from '../../../components/load';
import Search from '../../../components/search';
import MenuBar from '../../../components/menu-bar';
import WarnModal from '../../../components/modais/warn';
import ConsultantItem from './item-consultant';
import ConsultantModal from '../../../components/modais/consultant';
import ResponsiveContainer from '../../../components/container';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import { AdvisorCRUD } from '../../../services/requests/advisor';
import { useAppSelector } from '../../../hooks/useRedux';

import typ from './index.d';

const advisorCRUD = new AdvisorCRUD();

const ConsultantsPage: React.FC = () => {
  const { colors } = useTheme();

  const currentUser = useAppSelector(({state}) => state.user)

  // States
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [advisors, setAdvisors] = React.useState<typ.advisor[]>();
  const [showWarn, setShowWarn] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [currentAdvisor, setCurrentAdvisor] = React.useState<typ.advisor>();

  const currentData = React.useMemo(() => {
    if (search) {
      let currentFilter = search.toLocaleLowerCase();
      return advisors?.filter(e =>
        e.name.toLowerCase().includes(currentFilter),
      );
    }
    return advisors;
  }, [advisors, search]);

  // Callbacks
  const getAdvisors = React.useCallback(async () => {
    try {
      setLoading(true);
      const { data, status, error } = await advisorCRUD.read({ page: 1, per_page: 20 });

      if (error) throw new Error('Não foi possivel listar os consultores');

      if (status === 200 && data) {
        setAdvisors(data.Advisor);
      }
      
    } catch (e) {
      const { message } = e as Error;

      toast.error(message)
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAdvisor = React.useCallback(async (id: number) => {
    try {
      setLoading(true);
      const { status, error } = await advisorCRUD.delete({ id });

      if (error) throw new Error('Não foi possivel deletar o consultor. Tente novamente mais tarde');
      if (status === 200) {
        getAdvisors();
        toast('Consultor deletado com sucesso.');
      }
    } catch (e) {
      const {message} = e as Error;
      
      toast.error(message)
    } finally {
      setLoading(false);
      setCurrentAdvisor(undefined);
    }
  }, []);

  // Handles
  const handleEditAdvisor = React.useCallback((currentAdvisor: typ.advisor) => {
    if (currentUser?.role !== 'user' || currentUser.id === currentAdvisor.id) {
      setCurrentAdvisor(currentAdvisor);
      setShowModal(true);
      return;
    }
      toast('Você não tem permissão para editar um consultor. Peça á alguém que tenha acesso ADMIN')
  }, [])

  const handleDeleteAdvisor = React.useCallback((currentAdvisor: typ.advisor) => {
    if(currentUser?.role !== 'user') {
      setCurrentAdvisor(currentAdvisor);
      setShowWarn(true);
      return;
    }
    toast('Você não tem permissão para deletar um consultor. Peça á alguém que tenha acesso ADMIN')
  }, [])

  // Efffects
  React.useEffect(() => {
    getAdvisors();
  }, []);

  return (
    <Container>
      <MenuBar currentPage="Consultores" />
      <Content>
        <ResponsiveContainer>
          <Header>
            <HeaderBack href="/">
              <BackFullIcon />
            </HeaderBack>
            <HeaderInfo>
              <HeaderTitle>Todos os consultores</HeaderTitle>
              <HeaderActions>
                <Search
                  value={search}
                  setValue={setSearch}
                  placeholder="Pesquisar Consultores"
                />
                <AddConsultantButton
                  title="Adicionar consultor"
                  iconRight={AddIcon}
                  callback={() => {
                    if(currentUser?.role !== 'user') {
                      setShowModal(true);
                      return;
                    }
                    toast('Você não tem permissão para criar um consultor')
                  }}
                />
              </HeaderActions>
            </HeaderInfo>
          </Header>
          <ConsultantsContainer>
            {loading ? (
              <LoadRow>
                <Load
                  customStyle={{
                    color: colors.secondaryDark,
                  }}
                  size="extraLarge"
                />
              </LoadRow>
            ) : (
              <>
                <DescriptionRow>
                  <DescriptionText style={{ flex: 3 }}>
                    Nome
                  </DescriptionText>
                  <DescriptionText style={{ flex: 4 }}>
                    Email
                  </DescriptionText>
                  <DescriptionText style={{ flex: 2 }}>
                    Criado em
                  </DescriptionText>
                  <DescriptionText style={{ flex: 0 }}>
                    Ação
                  </DescriptionText>
                </DescriptionRow>

                {currentData && currentData.length > 0 ? (
                    currentData?.map((e, i) => ( 
                      <ConsultantItem 
                        key={i}  
                        data={e}   
                        handleEdit={handleEditAdvisor}  
                        handleDelete={handleDeleteAdvisor} 
                      /> 
                    )) 
                  ) : ( 
                  <HeaderTitle>Nenhuma consultor encontrado...</HeaderTitle>
                )} 
              </>
            )}
          </ConsultantsContainer>
        </ResponsiveContainer>
      </Content>
      {showModal && (
        <ConsultantModal
          onClose={() => {
            setCurrentAdvisor(undefined);
            setShowModal(false);
            getAdvisors();
          }}
          consultantData={currentAdvisor}
        />
      )}
      {showWarn && currentAdvisor && (
        <WarnModal
          title="Remover consultante"
          description={`Certeza que quer remover ${currentAdvisor.name}?`}
          declineButtonTitle='Não, manter.'
          confirmButtonTitle='Sim, remover.'
          confirmCallback={() => {
            deleteAdvisor(currentAdvisor?.id || 0);
            setShowWarn(false);
          }}
          declineCallback={() => {
            setCurrentAdvisor(undefined);
            setShowWarn(false);
          }}
        />
      )}
    </Container>
  );
};

export default ConsultantsPage;
